import {
    createAsyncThunk,
    createEntityAdapter,
    createSelector,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import { assetUrl } from '@/utils';
import type { Guest, GuestsStatus, RawGuest } from '@/types/guest';

const adapter = createEntityAdapter<Guest>({
    sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const initialState = adapter.getInitialState({
    status: 'idle' as GuestsStatus,
    error: null as string | null,
});

const normalizeArray = (raw: unknown): RawGuest[] => {
    if (Array.isArray(raw)) return raw as RawGuest[];
    if (raw && typeof raw === 'object' && Array.isArray((raw as any).items)) {
        return (raw as any).items as RawGuest[];
    }
    return [];
};

const toGuest = (g: RawGuest) => ({
    id: Number(g._id),
    name: String(g.name ?? ''),
    pathname: g.pathname ? String(g.pathname) : undefined,
    photo: g.photo ? String(g.photo) : undefined,
    about: g.about ? String(g.about) : undefined,
});

export const loadGuests = createAsyncThunk<Guest[], void, { rejectValue: string }>(
    'guests/load',
    async (_, { rejectWithValue }) => {
        try {
            const res = await fetch(assetUrl('guests.json'), { cache: 'no-cache' });
            if (!res.ok) return rejectWithValue(`Network error: ${res.status}`);
            const raw = await res.json();
            return normalizeArray(raw).map(toGuest).filter((g) => Number.isFinite(g.id) && g.name);
        } catch (e: any) {
            return rejectWithValue(e?.message ?? 'Failed to load guests');
        }
    }
);

export const guestsSlice = createSlice({
    name: 'guests',
    initialState,
    reducers: {},
    extraReducers: (b) => {
        b.addCase(loadGuests.pending, (s) => {
            s.status = 'loading';
            s.error = null;
        });
        b.addCase(loadGuests.fulfilled, (s, a: PayloadAction<Guest[]>) => {
            s.status = 'succeeded';
            adapter.setAll(s, a.payload);
        });
        b.addCase(loadGuests.rejected, (s, a) => {
            s.status = 'failed';
            s.error = (a.payload as string) ?? 'Unknown error';
        });
    },
});

export const guestsReducer = guestsSlice.reducer;

// selectors
const base = (s: RootState) => (s as any).guests ?? initialState;

export const { selectAll: selectAllGuests } = adapter.getSelectors<RootState>(base);

export const selectGuestsStatus = (s: RootState) => base(s).status;
export const selectGuestsError = (s: RootState) => base(s).error;

export const selectGuestsByIdMap = createSelector([selectAllGuests], (all) => {
    const map = new Map<number, Guest>();
    for (const g of all) map.set(g.id, g);
    return map;
});
